import Modal from "components/modal/Modal";
import ModalSizes from "enums/modalSizes";
import { CommunicationChannels, TemplateTypes } from "enums";
import NotifyWizardStep from "components/notifyStep/NotifyWizardStep/NotifyWizardStep";
import { Button } from "components/buttons/button/Button";
import styles from "./NotifyUsersModal.module.scss";
import { MultiSelect } from "components/multiSelect";
import React, { useEffect, useMemo, useRef, useState } from "react";
import FieldLabel from "components/forms/FieldLabel";
import { Badge } from "components";
import { noop, uniq } from "lodash";
import { Message } from "semantic-ui-react";
import { useGetCountQuery } from "features/Library/Flows/state/api/usersCountByFlowStatusApi";
import type { INotifyByEmailController } from "components/notifyStep/notifyByEmailController";
import { mappedStatusLevels, normalizeStatusFilter } from "features/Library/Common/utils/performanceUtils";
import { notifyFlowUsers } from "features/Library/Flows/state/thunks/flowUsersThunk";
import type { AppDispatch } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { bindAction } from "interfaces";
import type { FlowPriorityModal } from "../../types/performance";

type Props = PropsFromRedux & {
  flowInfos: FlowPriorityModal[];
  onNotifySuccess: () => void;
  appliedStatuses?: number[];
  renderTrigger?: (onOpen: () => void) => React.ReactNode;
};

const NotifyUsersModal = ({ flowInfos, onNotifySuccess, renderTrigger, appliedStatuses, onNotify }: Props) => {
  const [notifying, setNotifying] = useState(false);
  const [userStatus, setUserStatus] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);

  const notifyByEmailControllerRef = useRef<INotifyByEmailController | null>(null);

  const normalizedStatuses = useMemo(() => normalizeStatusFilter(userStatus), [userStatus]);

  useEffect(() => {
    if (appliedStatuses && appliedStatuses.length > 0) {
      setUserStatus((statuses) => uniq([...statuses, ...appliedStatuses]));
    }
  }, [appliedStatuses]);

  const {
    data: count,
    isLoading,
    isFetching,
  } = useGetCountQuery(
    {
      flowInfos,
      statuses: normalizedStatuses,
    },
    { skip: !isOpen },
  );

  const handleStatusChange = async ({ value }: { value: number[] }) => {
    setUserStatus(value);
  };

  const handleNotify = async () => {
    try {
      setNotifying(true);
      const notificationSettings = await notifyByEmailControllerRef.current!.getSettings(
        true,
        CommunicationChannels.Email,
      );

      const requests: Record<number, number[]> = {};
      for (const flowInfo of flowInfos) {
        if (flowInfo.FlowPriority === null) {
          continue;
        }
        if (!requests[flowInfo.FlowPriority]) {
          requests[flowInfo.FlowPriority] = [];
        }
        requests[flowInfo.FlowPriority].push(flowInfo.FlowId);
      }

      await Promise.all(
        Object.entries(requests).map(([priority, flowIds]) =>
          onNotify({
            FlowIds: flowIds,
            Statuses: normalizedStatuses,
            Priorities: [Number(priority)],
            NotificationSettings: notificationSettings,
          }),
        ),
      );
      setIsOpen(false);
      setError(null);
      onNotifySuccess();
    } catch (error) {
      setError(error?.message || "Failed to notify users");
    } finally {
      setNotifying(false);
      setUserStatus([]);
      notifyByEmailControllerRef.current = null;
      setIsTemplateLoaded(false);
    }
  };

  const handleNotifyStepLoaded = (notifyByEmailController: INotifyByEmailController) => {
    setIsTemplateLoaded(true);
    notifyByEmailControllerRef.current = notifyByEmailController;
  };

  const handleClose = () => {
    setIsOpen(false);
    setUserStatus([]);
    setError(null);
    notifyByEmailControllerRef.current = null;
    setIsTemplateLoaded(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  return (
    <Modal
      title="Notify users"
      open={isOpen}
      onCancel={handleClose}
      size={ModalSizes.large}
      alert={error ? <Message error>{error}</Message> : null}
      renderTrigger={() => renderTrigger?.(handleOpen)}
      renderModalActions={() => (
        <div className={styles.buttons}>
          <Button color={"blue"} basic onClick={handleClose}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleNotify}
            loading={notifying}
            disabled={!count || isLoading || isFetching || !isTemplateLoaded}
          >
            Send Email Now
          </Button>
        </div>
      )}
    >
      <div className={styles.recipients}>
        <div className={styles.statusSelector}>
          <div className={styles.label}>
            <FieldLabel label={"Notify users whose status is"} />
          </div>
          <MultiSelect
            propertyName={"userStatus"}
            items={mappedStatusLevels}
            selected={userStatus}
            onMultiSelectChange={handleStatusChange}
          />
        </div>

        <div className={styles.counter}>
          <div className={styles.label}>
            <FieldLabel label={"Recipients"} />
          </div>
          <div className={styles.count}>
            <Badge size={"large"} count={count || 0} color="primary" alignment="center" />
          </div>
        </div>
      </div>

      <NotifyWizardStep
        templateType={TemplateTypes.FlowKeepLearning}
        communicationChannel={CommunicationChannels.Email}
        shouldNotify
        onLoaded={handleNotifyStepLoaded}
        onValidChange={noop}
      />
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onNotify: bindAction(notifyFlowUsers, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(NotifyUsersModal);
export default ConnectedComponent;
